// Per-page SEO helper.
//
// React SPAs render <head> client-side, but Googlebot now executes JavaScript
// and reads the rendered DOM — so dynamically injected VideoObject JSON-LD
// is picked up for Google Video Search rich results.
//
// On unmount we restore the default site-wide tags so navigating away from
// a video page doesn't leak its metadata into other routes.

const MANAGED_ATTR = 'data-aivue-dynamic-seo';

function setOrCreateMeta({ key, name, property, content }) {
  if (!content) return;
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    if (name) el.setAttribute('name', name);
    if (property) el.setAttribute('property', property);
    el.setAttribute(MANAGED_ATTR, key);
    document.head.appendChild(el);
  } else if (!el.hasAttribute(MANAGED_ATTR)) {
    // Stash the original so we can restore on unmount
    el.setAttribute('data-aivue-original', el.getAttribute('content') || '');
    el.setAttribute(MANAGED_ATTR, key);
  }
  el.setAttribute('content', content);
}

function setOrCreateLink({ key, rel, href }) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(MANAGED_ATTR, key);
    document.head.appendChild(el);
  } else if (!el.hasAttribute(MANAGED_ATTR)) {
    el.setAttribute('data-aivue-original', el.getAttribute('href') || '');
    el.setAttribute(MANAGED_ATTR, key);
  }
  el.setAttribute('href', href);
}

function injectJsonLd(key, payload) {
  // Remove any prior dynamic JSON-LD with the same key
  document.querySelectorAll(`script[${MANAGED_ATTR}="${key}"]`).forEach((n) => n.remove());
  const s = document.createElement('script');
  s.type = 'application/ld+json';
  s.setAttribute(MANAGED_ATTR, key);
  s.textContent = JSON.stringify(payload);
  document.head.appendChild(s);
}

/**
 * Apply VideoObject SEO to <head>. Returns a cleanup fn to restore site defaults.
 */
export function applyVideoSeo({ video, videoUrl, thumbnailUrl, pageUrl }) {
  if (!video) return () => {};

  const title = `${video.title || 'AI Music Video'} — ${video.artist || 'AUDIVUE'} | AUDIVUE`;
  const desc = (
    video.description?.trim() ||
    `Watch "${video.title}" by ${video.artist} on AUDIVUE — the #1 streaming and discovery platform for AI music videos. Genre: ${video.genre || 'AI Music'}.`
  ).slice(0, 300);
  const url = pageUrl || (typeof window !== 'undefined' ? window.location.href : '');

  document.title = title;

  // Standard SEO meta tags
  setOrCreateMeta({ key: 'video-title', name: 'title', content: title });
  setOrCreateMeta({ key: 'video-description', name: 'description', content: desc });
  setOrCreateMeta({
    key: 'video-keywords',
    name: 'keywords',
    content: [
      video.title,
      video.artist,
      video.genre,
      video.style,
      'AI music video',
      'AI music',
      'AI generated music',
      'AUDIVUE',
    ].filter(Boolean).join(', '),
  });

  // Open Graph (Facebook, LinkedIn, iMessage, Discord)
  setOrCreateMeta({ key: 'og-type', property: 'og:type', content: 'video.other' });
  setOrCreateMeta({ key: 'og-title', property: 'og:title', content: title });
  setOrCreateMeta({ key: 'og-desc', property: 'og:description', content: desc });
  setOrCreateMeta({ key: 'og-url', property: 'og:url', content: url });
  if (thumbnailUrl) setOrCreateMeta({ key: 'og-image', property: 'og:image', content: thumbnailUrl });
  if (thumbnailUrl) setOrCreateMeta({ key: 'og-image-secure', property: 'og:image:secure_url', content: thumbnailUrl });
  if (videoUrl) {
    setOrCreateMeta({ key: 'og-video', property: 'og:video', content: videoUrl });
    setOrCreateMeta({ key: 'og-video-secure', property: 'og:video:secure_url', content: videoUrl });
    setOrCreateMeta({ key: 'og-video-type', property: 'og:video:type', content: 'video/mp4' });
  }

  // Twitter / X
  setOrCreateMeta({ key: 'tw-card', name: 'twitter:card', content: videoUrl ? 'player' : 'summary_large_image' });
  setOrCreateMeta({ key: 'tw-title', name: 'twitter:title', content: title });
  setOrCreateMeta({ key: 'tw-desc', name: 'twitter:description', content: desc });
  setOrCreateMeta({ key: 'tw-url', name: 'twitter:url', content: url });
  if (thumbnailUrl) setOrCreateMeta({ key: 'tw-image', name: 'twitter:image', content: thumbnailUrl });
  if (videoUrl) {
    setOrCreateMeta({ key: 'tw-player', name: 'twitter:player', content: url });
    setOrCreateMeta({ key: 'tw-player-stream', name: 'twitter:player:stream', content: videoUrl });
    setOrCreateMeta({ key: 'tw-player-stream-type', name: 'twitter:player:stream:content_type', content: 'video/mp4' });
    setOrCreateMeta({ key: 'tw-player-w', name: 'twitter:player:width', content: '1280' });
    setOrCreateMeta({ key: 'tw-player-h', name: 'twitter:player:height', content: '720' });
  }

  // Canonical
  setOrCreateLink({ key: 'canonical', rel: 'canonical', href: url });

  // VideoObject JSON-LD — the key SEO win for Google Video Search
  const uploadedISO = video.created_at || new Date().toISOString();
  injectJsonLd('video-jsonld', {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: video.title || 'Untitled',
    description: desc,
    thumbnailUrl: thumbnailUrl ? [thumbnailUrl] : undefined,
    uploadDate: uploadedISO,
    contentUrl: videoUrl || undefined,
    embedUrl: url,
    duration: video.duration ? `PT${Math.round(video.duration)}S` : undefined,
    genre: video.genre || undefined,
    inLanguage: 'en',
    isFamilyFriendly: true,
    publisher: {
      '@type': 'Organization',
      name: 'AUDIVUE',
      logo: { '@type': 'ImageObject', url: 'https://audivue.io/logo.png' },
    },
    creator: video.artist
      ? { '@type': 'Person', name: video.artist, url: `https://audivue.io/creator/${video.user_id || video.artist_id || ''}` }
      : undefined,
    author: video.artist ? { '@type': 'Person', name: video.artist } : undefined,
    interactionStatistic: [
      {
        '@type': 'InteractionCounter',
        interactionType: { '@type': 'WatchAction' },
        userInteractionCount: video.views || 0,
      },
      {
        '@type': 'InteractionCounter',
        interactionType: { '@type': 'LikeAction' },
        userInteractionCount: video.likes || 0,
      },
    ],
    keywords: [
      video.title,
      video.artist,
      video.genre,
      video.style,
      'AI music video',
      'AI music',
    ].filter(Boolean).join(', '),
  });

  // BreadcrumbList JSON-LD — improves "you are here" indexing
  injectJsonLd('video-breadcrumb-jsonld', {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://audivue.io/' },
      { '@type': 'ListItem', position: 2, name: 'Discover', item: 'https://audivue.io/discover' },
      ...(video.genre
        ? [{ '@type': 'ListItem', position: 3, name: video.genre, item: `https://audivue.io/discover?genre=${encodeURIComponent(video.genre)}` }]
        : []),
      { '@type': 'ListItem', position: video.genre ? 4 : 3, name: video.title || 'Video', item: url },
    ],
  });

  // Cleanup: restore prior values when navigating away
  return () => {
    document.querySelectorAll(`[${MANAGED_ATTR}]`).forEach((el) => {
      const original = el.getAttribute('data-aivue-original');
      if (original !== null) {
        if (el.tagName === 'LINK') el.setAttribute('href', original);
        else el.setAttribute('content', original);
        el.removeAttribute('data-aivue-original');
        el.removeAttribute(MANAGED_ATTR);
      } else {
        // Tag we created — remove it entirely
        el.remove();
      }
    });
    // Reset document.title to the default
    document.title = 'AUDIVUE — The Home of AI Music Videos.';
  };
}
